import {
  DiscountType,
  ActivityGroupType,
  FullDiscountPresentType,
  SelectiveGiftActivityType,
  ShoppingCreditConditionType,
  _PromotionType,
} from '@/types';
import type { Common } from '@/types/global-business';
import NP from 'number-precision';
import { commonUtils } from '../common';
import { businessUtils } from '../business';
import { useUserStore } from './../../store';

type Config = {
  name: string | string[];
  description: string;
  startTime: string;
  endTime: string;
};

/**
 * 根据优惠类型获取优惠单位
 * @param discountType - 优惠类型
 * @returns
 */
const getMinimumByFullDiscountPresentType = (type: FullDiscountPresentType, num: number) => {
  return type !== FullDiscountPresentType.QuantityFullReduction
    ? `${NP.divide(num, 100)}元`
    : `${num}件`;
};

/**
 * 根据优惠类型获取优惠单位
 * @param discountType - 优惠类型
 * @returns
 */
const getMinimumBySelectiveGiftActivityType = (type: SelectiveGiftActivityType, num: number) => {
  return type !== SelectiveGiftActivityType.quantity
    ? `${NP.divide(num, 100)}元`
    : `${num}件`;
};

/**
 * 根据优惠类型获取优惠单位
 * @param discountType - 优惠类型
 * @returns
 */
const getUnitByDiscountType = (discountType: DiscountType) => {
  return discountType === DiscountType.FixedAmount
    ? '元'
    : discountType === DiscountType.FixedPercent
    ? '折'
    : '';
};

/**
 * 根据优惠类型获取优惠动作
 * @param discountType - 优惠类型
 * @returns
 */
const getActionByDiscountType = (discountType: DiscountType) => {
  return discountType === DiscountType.FixedAmount
    ? '减'
    : discountType === DiscountType.FixedPercent
    ? '打'
    : '';
};

/**
 * 根据优惠类型获取换算后优惠数值
 * @param discountType - 优惠类型
 * @param discount - 原优惠数值
 * @returns
 */
const getValueByDiscountType = (discountType: DiscountType, discount: number) => {
  return discountType === DiscountType.FixedAmount
    ? NP.divide(discount, 100)
    : discountType === DiscountType.FixedPercent
    ? NP.divide(discount, 10)
    : 0;
};

const discountActivityHandler = (config: Config, data: AnyObject) => {
  const n = data.minimum;
  // 5折和0折特殊处理
  if (data.discount === 50) {
    config.name = `第${n}件半价`;
    config.description = `每满${n}件，第${n}件享半价优惠`;
  } else if (data.discount === 0) {
    config.name = `第${n}件0元`;
    config.description = `每满${n}件，第${n}件享0元优惠`;
  } else {
    const d = NP.divide(data.discount, 10);
    config.name = `第${n}件${d}折`;
    config.description = `每满${n}件，第${n}件享${d}折优惠`;
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const discountActivityLabelHandler = (data: AnyObject) => {
  const n = data.minimum;
  // 5折和0折特殊处理
  if (data.discount === 50) {
    return `第${n}件半价`;
  } else if (data.discount === 0) {
    return `第${n}件0元`;
  } else {
    const d = NP.divide(data.discount, 10);
    return `第${n}件${d}折`;
  }

};

const fullDiscountPresentHandler = (config: Config, data: AnyObject) => {
  // 默认标题满减
  config.name = `满减`;

  const computeHandler = (it: AnyObject) => {
    const discountType = it.discountValue.discountType;
    const discount = it.discountValue.discount;
    const value = getValueByDiscountType(discountType, discount);
    const unit = getUnitByDiscountType(discountType);
    const action = getActionByDiscountType(discountType);
    return { value, unit, action };
  };

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];
    const result = computeHandler(it);

    let _description = '';

    // 0折特殊处理
    if (it.discountValue.discountType === DiscountType.noDiscount) {
      _description = `每满${getMinimumByFullDiscountPresentType(
        data.fullDiscountType,
        it.minimum
      )}送赠品`;

      // 标题显示满赠
      config.name = `满赠`;
    } else {
      _description = `每满${getMinimumByFullDiscountPresentType(
        data.fullDiscountType,
        it.minimum
      )}${result.action}${result.value}${result.unit}`;
    }

    config.description = _description;
  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠
    data.ruleValues.forEach((it: AnyObject, index: number) => {
      const result = computeHandler(it);

      let _description = '';

      // 0折特殊处理
      if (it.discountValue.discountType === DiscountType.noDiscount) {
        _description = `满${getMinimumByFullDiscountPresentType(
          data.fullDiscountType,
          it.minimum
        )}送赠品`;

        // 标题显示满赠
        config.name = `满赠`;
      } else {
        _description = `满${getMinimumByFullDiscountPresentType(
          data.fullDiscountType,
          it.minimum
        )}${result.action}${result.value}${result.unit}`;
      }

      if (index === data.ruleValues.length - 1) {
        config.description += _description;
      } else {
        config.description += `${_description}，`;
      }
    });
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  // 实付满赠 标题 特殊处理
  if (data.fullDiscountType === FullDiscountPresentType.AmountFullPresent) {
    config.name = '实付满赠';
  }

  return config;
};

const fullDiscountPresentLabelHandler = (data: AnyObject, price: number, isVipProduct: boolean) => {
  const userStore = useUserStore();
  
  let text = "";
  let rate = 1;
  
  // 检查是否是VIP产品，并设置相应的文本和折扣率
  const vipText = isVipProduct ? "年卡 " : "";
  const vipRate = isVipProduct ? businessUtils.commodity.getMemberCardRate() : 1;
  
  // 只有在需要限制用户且组类型为会员计划时才需要额外判断
  if (data?.whetherRestrictUsers && data?.groupType === ActivityGroupType.MemberPlan && userStore.isLogin && !userStore.isMember) {
    // 已登录非会员用户有特殊处理时在此添加
    text = "";
    rate = 1;
  } else {
    // 其他所有情况都使用相同的处理
    text = vipText;
    rate = vipRate;
  }
  
  const computeHandler = (it: AnyObject) => {
    const discountType = it.discountValue.discountType;
    const discount = it.discountValue.discount;
    const value = getValueByDiscountType(discountType, discount);
    const unit = getUnitByDiscountType(discountType);
    const action = getActionByDiscountType(discountType);
    return { value, unit, action };
  };

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];
    const result = computeHandler(it);

    // 0折特殊处理
    if (it.discountValue.discountType === DiscountType.noDiscount) {
      return ""
    } else {
      if (data.fullDiscountType !== FullDiscountPresentType.QuantityFullReduction) {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `每满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}减${result.value}元`;
        } else {
          return text + `每满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}预计单件${businessUtils.price.roundedUp(NP.times(NP.divide(price, 100) * NP.divide(result.value, 10), rate))}元起`;
        }
      } else {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `每买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.times(NP.divide((NP.times(NP.divide(price, 100), it.minimum) - result.value), it.minimum), rate))}元起`
        } else {
          return text + `每买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.times(NP.divide((NP.times(NP.divide(price, 100), it.minimum) * NP.divide(result.value, 10)), it.minimum), rate))}元起`
        }
      }
    }
  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠

    const it = data.ruleValues[0];
    const result = computeHandler(it);

    // 0折特殊处理
    if (it.discountValue.discountType === DiscountType.noDiscount) {
      return ""
    } else {
      if (data.fullDiscountType !== FullDiscountPresentType.QuantityFullReduction) {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}减${result.value}元`;
        } else {
          return text + `满${getMinimumByFullDiscountPresentType(
            data.fullDiscountType,
            it.minimum
          )}预计单件${businessUtils.price.roundedUp(NP.times(NP.divide(price, 100) * NP.divide(result.value, 10), rate))}元起`;
        }
      } else {
        if (it.discountValue.discountType === DiscountType.FixedAmount) {
          return text + `买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.times(NP.divide((NP.times(NP.divide(price, 100), it.minimum) - result.value), it.minimum), rate))}元起`
        } else {
          return text + `买${it.minimum}件预计单件${businessUtils.price.roundedUp(NP.times(NP.divide((NP.times(NP.divide(price, 100), it.minimum) * NP.divide(result.value, 10)), it.minimum), rate))}元起`
        }
      }
    }
  }

  return ""
};

const selectiveGiftActivityHandler = (config: Config, data: AnyObject) => {
  config.name = `任选满赠`;

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];
    config.description = `每满${getMinimumBySelectiveGiftActivityType(
        data.selectiveGiftActivityType,
        it.minimum
      )}送赠品`;

  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠
    data.ruleValues.forEach((it: AnyObject, index: number) => {
      const _description = `满${getMinimumBySelectiveGiftActivityType(
          data.selectiveGiftActivityType,
          it.minimum
        )}送赠品`;
      if (index === data.ruleValues.length - 1) {
        config.description += _description;
      } else {
        config.description += `${_description}，`;
      }
    });
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const shoppingCreditsDeductionHandler = (config: Config, data: AnyObject) => {
  config.name = data.name;
  config.description =
    data.minimum === 0
      ? `年卡用户可抵实付金额${data.memberDeductionRate}%购物金，非年卡用户可抵实付金额${data.deductionRate}%购物金`
      : `满${NP.divide(data.minimum, 100)}元年卡用户可抵实付金额${data.memberDeductionRate}%购物金，非年卡用户可抵实付金额${data.deductionRate}%购物金`;
  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const shoppingCreditsDeductionLabelHandler = (data: AnyObject, price: number) => {
  if (data?.memberDeductionRate && data.memberDeductionRate > data.deductionRate) {
    return `购物金最高可抵扣${businessUtils.price.roundedUp(NP.times(NP.divide(price, 100), NP.divide(data.memberDeductionRate, 100)))}元`
  }
  return `购物金可抵扣${businessUtils.price.roundedUp(NP.times(NP.divide(price, 100), NP.divide(data.deductionRate, 100)))}元`
};

const shoppingCreditsClaimHandler = (config: Config, data: AnyObject) => {
  config.name = data.name;

  // 兼容服务端字段类型错误问题
  data.ruleValues = data.shoppingCreditsClaimActivityRuleValues;

  const devide = (num: number) => NP.divide(num, 100);

  if (data.ruleType === 'cycle') {
    // 循环优惠
    const it = data.ruleValues[0];
    let _text = '';

    // 判断购物金条件类型
    if (data.shoppingCreditConditionType === ShoppingCreditConditionType.quantityFullReduction) {
      // 满件数
      // 固定百分比
      if (it.discountValue.discountType === DiscountType.FixedPercent) {
        _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可返商品实付金额${it.discountValue.discount}%的购物金` : `(年卡用户可返商品实付金额${it.discountValue.memberDiscount}%的购物金 非年卡用户可返商品实付金额${it.discountValue.discount}%的购物金)`;
        config.description = `每满${it.minimum}件${_text}，最高可获得${devide(it.maximumOffer)}元购物金`;
      } else {
        // 固定金额
        _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可获得${devide(it.discountValue.discount)}元购物金` : `(年卡用户可获得${devide(it.discountValue.memberDiscount)}元购物金 非年卡用户可获得${devide(it.discountValue.discount)}元购物金)`;
        config.description = `每满${it.minimum}件${_text}，最高可获得${devide(it.maximumOffer)}元购物金`;
      }
    } else {
      // 满金额
      // 固定百分比
      if (it.discountValue.discountType === DiscountType.FixedPercent) {
        _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可返商品实付金额${it.discountValue.discount}%的购物金` : `(年卡用户可返商品实付金额${it.discountValue.memberDiscount}%的购物金 非年卡用户可返商品实付金额${it.discountValue.discount}%的购物金)`;
        config.description = `每满${devide(it.minimum)}元${_text}，最高可获得${devide(it.maximumOffer)}元购物金`;
      } else {
        // 固定金额
        _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可获得${devide(it.discountValue.discount)}元购物金` : `(年卡用户可获得${devide(it.discountValue.memberDiscount)}元购物金 非年卡用户可获得${devide(it.discountValue.discount)}元购物金)`;
        config.description = `每满${devide(it.minimum)}元${_text}，最高可获得${devide(it.maximumOffer)}元购物金`;
      }
    }
  } else if (data.ruleType === 'ladder') {
    // 阶梯优惠
    data.ruleValues.forEach((it: AnyObject, index: number) => {
      let _description = '';
      let _text = '';

      if (it.minimum === 0) {
        _description = `购物金最高抵扣${devide(it.discountValue.discount)}%`;
      } else {
        // 判断购物金条件类型
        if (
          data.shoppingCreditConditionType === ShoppingCreditConditionType.quantityFullReduction
        ) {
          // 满件数
          // 固定百分比
          if (it.discountValue.discountType === DiscountType.FixedPercent) {
            _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可返商品实付金额的${it.discountValue.discount}%购物金` : `年卡用户可返商品实付金额的${it.discountValue.memberDiscount}%购物金 非年卡用户可返商品实付金额的${it.discountValue.discount}%购物金`;
            _description = `买满${it.minimum}件,${_text}`;
          } else {
            // 固定金额
            _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可获得${devide(it.discountValue.discount)}元购物金` : `年卡用户可获得${devide(it.discountValue.memberDiscount)}元购物金 非年卡用户可获得${devide(it.discountValue.discount)}元购物金`;
            _description = `满${it.minimum}件${_text}`;
          }
        } else {
          // 满金额
          // 固定百分比
          if (it.discountValue.discountType === DiscountType.FixedPercent) {
            _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可返商品实付金额的${it.discountValue.discount}%购物金` : `年卡用户可返商品实付金额的${it.discountValue.memberDiscount}%购物金 非年卡用户可返商品实付金额的${it.discountValue.discount}%购物金`;
            _description = `买满${devide(it.minimum)}元,${_text}`;
          } else {
            // 固定金额
            _text = it.discountValue.discount === it.discountValue.memberDiscount ? `可获得${devide(it.discountValue.discount)}元购物金` : `年卡用户可获得${devide(it.discountValue.memberDiscount)}元购物金 非年卡用户可获得${devide(it.discountValue.discount)}元购物金`;
            _description = `满${devide(it.minimum)}元${_text}`;
          }
        }
      }

      if (index === data.ruleValues.length - 1) {
        config.description += _description;
      } else {
        config.description += `${_description}，`;
      }
    });
  }

  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const purchasePremiumHandler = (config: Config, data: AnyObject) => {
  config.name = `换购`;
  config.description = `满${NP.divide(data.minimum, 100)}元享优惠换购`;
  config.startTime = commonUtils.date.format(data.validityPeriod.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.validityPeriod.endTime, 'YYYY.MM.DD');

  return config;
};

const packageDiscountHandler = (config: Config, data: AnyObject) => {
  config.name = '打包一口价';
  config.description = data?.activityContentType[0];
  config.startTime = commonUtils.date.format(data.startTime, 'YYYY.MM.DD');
  config.endTime = commonUtils.date.format(data.endTime, 'YYYY.MM.DD');

  return config;
};

const packageDiscountLabelHandler = (data: AnyObject) => {
  return `一口价${NP.divide(data?.price, 100)}元${data?.selectCount}件 单件${businessUtils.price.roundedUp(NP.divide(data?.price, 100) / data?.selectCount)}元起`;
};

export default {
  /**
   * 获取优惠活动配置信息
   * @param item
   * @returns
   */
  getConfig: (item: AnyObject | null) => {
    const config: Config = {
      name: '',
      description: '',
      startTime: '',
      endTime: '',
    };

    if (!item) return config;

    const actions: Common.RespondAction<_PromotionType, Config> = {
      // 活动 => eg:第二件半价
      [_PromotionType.DiscountActivity]: (conf) => discountActivityHandler(conf, item),

      // 活动 => 满减送
      [_PromotionType.FullDiscountPresent]: (conf) => fullDiscountPresentHandler(conf, item),

      // 加价购
      [_PromotionType.PurchasePremium]: (conf) => purchasePremiumHandler(conf, item),

      // 打包一口价
      [_PromotionType.PackageDiscount]: (conf) => packageDiscountHandler(conf, item),

      // 任选满赠
      [_PromotionType.SelectiveGiftActivity]: (conf) => selectiveGiftActivityHandler(conf, item),

      // 购物金获得
      [_PromotionType.ShoppingCreditsClaimActivity]: (conf) =>
        shoppingCreditsClaimHandler(conf, item),

      // 购物金抵扣
      [_PromotionType.ShoppingCreditsDeductionActivity]: (conf) =>
        shoppingCreditsDeductionHandler(conf, item),
    };

    actions[item.__typename as _PromotionType]?.(config);

    return config;
  },
  /**
   * 获取优惠活动明细配置标签
   * @param item
   * @returns
   */
  getLabelConfig: (item: AnyObject | null, price: number, isVipProduct: boolean) => {
    if (!item || !item?.showLabelInCommodity) return "";

    const actions: Common.RespondAction<_PromotionType, string> = {
      // 活动 => eg:第二件半价
      [_PromotionType.DiscountActivity]: () => discountActivityLabelHandler(item),

      // 活动 => 满减送
      [_PromotionType.FullDiscountPresent]: () => fullDiscountPresentLabelHandler(item, price, isVipProduct),

      // 加价购
      [_PromotionType.PurchasePremium]: () => "",

      // 打包一口价
      [_PromotionType.PackageDiscount]: () => packageDiscountLabelHandler(item),

      // 任选满赠
      [_PromotionType.SelectiveGiftActivity]: () => "",

      // 购物金获得
      [_PromotionType.ShoppingCreditsClaimActivity]: () => "",

      // 购物金抵扣
      [_PromotionType.ShoppingCreditsDeductionActivity]: () => shoppingCreditsDeductionLabelHandler(item, price),
    };

    return actions[item.__typename as _PromotionType]?.();
  },
  /**
   * 获取活动标签渲染数据
   * @param data
   * @returns
   */
  getParticipatingActivities: (data: AnyObject) => {
    const pa = data?.participatingActivities ?? [];
    if (!pa.length) return [];

    return pa.map((it: AnyObject) => {
      return { name: it.customFields.activityName };
    });
  },
};
